import { Component, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ApiCallingService } from "../../services/api-calling.service";
import { ConfigApi } from "../../config-api";
import { ToastrService } from "ngx-toastr";
import { NgForm } from "@angular/forms";
import { Globals } from "../../common/global";

const MIN_IMAGES = 1;
const MAX_IMAGES = 10;
const LIST_IMAGES_PREVIEW_COUNT = 2;

const MODAL_TITLE = {
  ADD: "Add Festival",
  EDIT: "Edit Festival",
  VIEW: "View Festival",
  DELETE: "Delete Festival",
} as const;

@Component({
  selector: "app-festivals",
  templateUrl: "./festivals.component.html",
  styleUrls: ["./festivals.component.css"],
})
export class FestivalsComponent implements OnInit {
  readonly MIN_IMAGES = MIN_IMAGES;
  readonly MAX_IMAGES = MAX_IMAGES;
  readonly LIST_IMAGES_PREVIEW_COUNT = LIST_IMAGES_PREVIEW_COUNT;
  readonly MODAL_TITLE = MODAL_TITLE;

  operatorId = "";
  list: any[] = [];
  totalCount = 0;
  page = 1;
  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 50];
  loading = false;
  modelTitle = "";
  detail: any = null;
  festivalImageFiles: File[] = [];
  festivalImagePreviews: string[] = [];
  festivalImagesError = "";
  previewImages: string[] = [];
  previewIndex = 0;

  constructor(
    private modalService: NgbModal,
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    public Globals: Globals
  ) {
    const user = this.Globals.getUser();
    this.operatorId = user?._id ?? user?.operator_id?._id ?? "";
  }

  ngOnInit(): void {
    this.getList();
  }

  getList(): void {
    if (!this.operatorId) {
      this.toastr.warning("Operator not found.");
      return;
    }
    this.loading = true;
    const pageIndex = Math.max(0, this.page - 1);
    const params = `operator_id=${this.operatorId}&page=${pageIndex}&limit=${this.pageSize}`;
    this.apiCallingService
      .callAPI({}, "GET", `${ConfigApi.URLS.OPERATOR_FESTIVAL.LIST}?${params}`)
      .subscribe({
        next: (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.list = res?.data ?? [];
            this.totalCount = res?.total ?? res?.count ?? 0;
          }
        },
        error: () => {},
        complete: () => (this.loading = false),
      });
  }

  onPageChange(p: number): void {
    this.page = p;
    this.getList();
  }

  onPageSizeChange(): void {
    this.page = 1;
    this.getList();
  }

  openModal(template: any, data: any, title: string): void {
    this.modalService.open(template, {
      centered: true,
      backdrop: "static",
      size: "lg",
    });
    this.modelTitle = title;
    this.festivalImageFiles = [];
    this.festivalImagePreviews = [];
    this.festivalImagesError = "";

    if (this.isEmptyData(data)) {
      this.detail = {
        name: "",
        isDefault: false,
        visible: true,
        startDate: "",
        endDate: "",
        festivalImages: [],
      };
    } else {
      const sourceImages = this.getFestivalImages(data);
      this.detail = {
        _id: data._id,
        name: data.name ?? "",
        isDefault: data.isDefault ?? false,
        visible: data.visible !== false,
        startDate: data.startDate ? this.formatDateForInput(data.startDate) : "",
        endDate: data.endDate ? this.formatDateForInput(data.endDate) : "",
        festivalImages: [...sourceImages],
        countryObject: data.countryObject ?? null,
      };
    }
  }

  getFestivalImages(item: any): string[] {
    return item?.images ?? item?.festivalImages ?? [];
  }

  private isEmptyData(data: any): boolean {
    return !data || (typeof data === "object" && Object.keys(data).length === 0);
  }

  private formatDateForInput(d: string): string {
    if (!d) return "";
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  closeModal(): void {
    this.modalService.dismissAll();
  }

  onFestivalImagesChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newFiles = input.files ? Array.from(input.files) : [];
    this.festivalImagesError = "";

    if (newFiles.length === 0) {
      input.value = "";
      return;
    }

    const existingCount = this.detail?.festivalImages?.length ?? 0;
    const currentNewCount = this.festivalImageFiles.length;
    const totalAfterAdd = existingCount + currentNewCount + newFiles.length;

    if (totalAfterAdd > MAX_IMAGES) {
      this.festivalImagesError = `Maximum ${MAX_IMAGES} images total (existing + new). You can add ${MAX_IMAGES - existingCount - currentNewCount} more.`;
      input.value = "";
      return;
    }

    newFiles.forEach((file) => {
      this.festivalImageFiles.push(file);
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.festivalImagePreviews.push(e.target?.result ?? "");
      };
      reader.readAsDataURL(file);
    });

    input.value = "";
  }

  removeFestivalImage(index: number): void {
    this.festivalImageFiles.splice(index, 1);
    this.festivalImagePreviews.splice(index, 1);
    this.festivalImagesError = "";
  }

  removeExistingImage(index: number): void {
    if (this.detail?.festivalImages?.length) {
      this.detail.festivalImages.splice(index, 1);
      this.festivalImagesError = "";
    }
  }

  validateForm(f: NgForm): boolean {
    this.festivalImagesError = "";
    const isAdd = this.modelTitle === MODAL_TITLE.ADD;
    const newCount = this.festivalImageFiles.length;
    const existingCount = this.detail?.festivalImages?.length ?? 0;
    const hasNewImages = newCount > 0;
    const hasExistingImages = existingCount > 0;
    const totalImages = existingCount + newCount;

    if (isAdd && !hasNewImages) {
      this.festivalImagesError = `At least ${MIN_IMAGES} image is required (max ${MAX_IMAGES}).`;
      return false;
    }
    if (isAdd && newCount > MAX_IMAGES) {
      this.festivalImagesError = `Maximum ${MAX_IMAGES} images allowed.`;
      return false;
    }
    if (!isAdd && !hasExistingImages && !hasNewImages) {
      this.festivalImagesError = `At least ${MIN_IMAGES} image is required (max ${MAX_IMAGES}).`;
      return false;
    }
    if (!isAdd && totalImages > MAX_IMAGES) {
      this.festivalImagesError = `Maximum ${MAX_IMAGES} images total (existing + new).`;
      return false;
    }

    if (!f.valid) {
      Object.keys(f.controls).forEach((k) => f.controls[k].markAsTouched());
      return false;
    }
    return true;
  }

  onSubmit(f: NgForm): void {
    if (!this.validateForm(f)) return;
    if (!this.operatorId) {
      this.toastr.warning("Operator not found.");
      return;
    }

    const isAdd = this.modelTitle === MODAL_TITLE.ADD;
    const hasFiles = this.festivalImageFiles.length > 0;
    const hasExistingImages =
      !isAdd && (this.detail?.festivalImages?.length ?? 0) > 0;

    if (isAdd || hasFiles) {
      this.submitWithFormData(isAdd);
    } else {
      this.submitAsJson(hasExistingImages ? this.detail.festivalImages : undefined);
    }
  }

  private submitWithFormData(isAdd: boolean): void {
    const formData = new FormData();
    formData.append("operator_id", this.operatorId);
    formData.append("name", this.detail.name);
    formData.append("isDefault", String(this.detail.isDefault));
    formData.append("visible", String(this.detail.visible !== false));
    formData.append("startDate", this.detail.startDate);
    formData.append("endDate", this.detail.endDate);

    if (!isAdd) {
      const existingUrls = this.detail.festivalImages || [];
      formData.append("existingImages", JSON.stringify(existingUrls));
    }

    for (const file of this.festivalImageFiles) {
      formData.append("festivalImages", file);
    }

    const url = isAdd
      ? ConfigApi.URLS.OPERATOR_FESTIVAL.CREATE
      : `${ConfigApi.URLS.OPERATOR_FESTIVAL.UPDATE}/${this.detail._id}`;
    const method = isAdd ? "POST" : "PUT";

    this.apiCallingService.callAPIwithFile(formData, method, url).subscribe({
      next: (res) => {
        if (this.apiCallingService.handleSuccess(res)) {
          this.toastr.success(res?.msg || "Festival saved successfully.");
          this.closeModal();
          this.getList();
        } else {
          this.toastr.error(res?.msg || "Failed to save festival.");
        }
      },
      error: (err) =>
        this.toastr.error(
          err?.error?.msg || err?.message || "Error saving festival."
        ),
    });
  }

  private submitAsJson(images?: string[]): void {
    const payload: any = {
      operator_id: this.operatorId,
      name: this.detail.name,
      isDefault: this.detail.isDefault,
      visible: this.detail.visible !== false,
      startDate: this.detail.startDate,
      endDate: this.detail.endDate,
    };
    if (images !== undefined) {
      payload.images = images;
    }
    const url = `${ConfigApi.URLS.OPERATOR_FESTIVAL.UPDATE}/${this.detail._id}`;
    this.apiCallingService.callAPI(payload, "PUT", url).subscribe({
      next: (res) => {
        if (this.apiCallingService.handleSuccess(res)) {
          this.toastr.success(res?.msg || "Festival updated successfully.");
          this.closeModal();
          this.getList();
        } else {
          this.toastr.error(res?.msg || "Failed to update festival.");
        }
      },
      error: (err) =>
        this.toastr.error(
          err?.error?.msg || err?.message || "Error updating festival."
        ),
    });
  }

  confirmDelete(): void {
    if (!this.detail?._id) return;
    this.apiCallingService
      .callAPI(
        {},
        "DELETE",
        `${ConfigApi.URLS.OPERATOR_FESTIVAL.DELETE}/${this.detail._id}`
      )
      .subscribe({
        next: (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(res?.msg || "Festival deleted.");
            this.closeModal();
            this.getList();
          } else {
            this.toastr.error(res?.msg || "Failed to delete festival.");
          }
        },
        error: (err) =>
          this.toastr.error(
            err?.error?.msg || err?.message || "Error deleting festival."
          ),
      });
  }

  trackById(_index: number, item: any): any {
    return item._id;
  }

  openImagePreview(images: string[], index: number, template: any): void {
    this.previewImages = images?.length ? images : [];
    this.previewIndex = Math.max(
      0,
      Math.min(index, this.previewImages.length - 1)
    );
    this.modalService.open(template, {
      centered: true,
      size: "lg",
      windowClass: "festival-image-preview-modal",
    });
  }

  closeImagePreview(): void {
    this.modalService.dismissAll();
  }

  prevPreviewImage(): void {
    if (!this.previewImages.length) return;
    this.previewIndex =
      this.previewIndex <= 0
        ? this.previewImages.length - 1
        : this.previewIndex - 1;
  }

  nextPreviewImage(): void {
    if (!this.previewImages.length) return;
    this.previewIndex =
      this.previewIndex >= this.previewImages.length - 1
        ? 0
        : this.previewIndex + 1;
  }
}
